import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { NavigatorWithConnection } from '../../store/errorReportingStore/types/NavigatorWithConnection';
import useErrorReportingStore from '../../store/errorReportingStore/useErrorReportingStore';
import useFeatureStore from '../../store/featureStore/useFeatureStore';
import useLeadChannelStore from '../../store/leadChannelStore/useLeadChannelStore';
import useUserStore from '../../store/userStore/useUserStore';

const ErrorReportingGuard = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const {
    computed: { hasCurrentUser },
    user,
  } = useUserStore();
  const { lastInteractedFlags } = useFeatureStore();
  const {
    computed: { brand },
  } = useLeadChannelStore();
  const { addBreadcrumb, setAdditionalData, setTag, setUser } =
    useErrorReportingStore();
  const { pathname } = useLocation();

  const getConnectionType = (): string => {
    // `connection` is non-standard and not present in lib.dom.
    const connection = (navigator as NavigatorWithConnection).connection;
    return connection?.effectiveType ?? 'unknown';
  };

  // Sync the authenticated user onto the error-reporting scope. Drop a
  // breadcrumb so the timeline shows when sign-in happened relative to the error.
  useEffect((): void => {
    if (!hasCurrentUser) {
      setUser(null);
      return;
    }

    setUser({ id: user.id, email: user.primaryEmailAddress });
    addBreadcrumb({
      category: 'auth',
      data: { userId: user.id },
      level: 'info',
      message: 'User identified',
    });
  }, [user]);

  // Surface the most recently interacted feature flags as extras (grouped
  // under a single key) and as a breadcrumb so we can correlate the user's
  // flag treatment with the failure.
  useEffect((): void => {
    if (isEmpty(lastInteractedFlags)) {
      return;
    }

    setAdditionalData({ featureFlags: lastInteractedFlags });
    addBreadcrumb({
      category: 'feature-flag',
      data: lastInteractedFlags,
      level: 'info',
      message: 'Feature flags updated',
    });
  }, [lastInteractedFlags]);

  // Tagging brand makes it filterable in the error-reporting UI.
  // Keep the extra too for backwards compatibility with existing dashboards.
  useEffect((): void => {
    setTag('brand', brand);
    setAdditionalData({ brand });
    addBreadcrumb({
      category: 'brand',
      data: { brand },
      level: 'info',
      message: `Brand set to ${brand}`,
    });
  }, [brand]);

  // Tag the current route so errors can be sliced by page in the dashboard
  // and so the breadcrumb timeline shows when navigation happened.
  useEffect((): void => {
    setTag('route', pathname);
    addBreadcrumb({
      category: 'navigation',
      data: { pathname },
      level: 'info',
      message: `Navigated to ${pathname}`,
    });
  }, [pathname]);

  // Tag the effective network type (`4g`, `3g`, `slow-2g`, etc.) so flaky
  // connections can be correlated with timeouts and aborted requests.
  useEffect((): void => {
    setTag('connectionType', getConnectionType());
  }, []);

  return children;
};

export default ErrorReportingGuard;
